// src/pages/VehicleV2/WorkLocationMaster.jsx

import React, { useEffect, useMemo, useState, useContext, useCallback } from "react";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { FaSave, FaUndo, FaArrowLeft } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/workLocationEntry";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const emptyForm = {
  workLocName: "",
  address1: "",
  address2: "",
  countryId: "",
  stateId: "",
  districtId: "",
  cityId: "",
  zipcode: "",
  contactPerson: "",
  email: "",
  mobile: "",
  phone: "",
  remarks: "",
  code: "",
  isClosed: false,
};

const WorkLocationMaster = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const branch = localStorage.getItem("branchType") || localStorage.getItem("branchtype") || "";
  const userId = localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const workLocationId = useMemo(
    () =>
      toInt(
        searchParams.get("worklocationid") ||
          searchParams.get("workLocationId") ||
          searchParams.get("id"),
        0
      ),
    [searchParams]
  );
  const isEdit = workLocationId > 0;

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [loadingDDL, setLoadingDDL] = useState(false);

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;
  const labelClass = `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const setField = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));

  const fillFormFromData = useCallback((w, codeFromInit = "") => {
    setFormData({
      workLocName: toStr(w?.workLocName),
      address1: toStr(w?.address1),
      address2: toStr(w?.address2),
      countryId: w?.countryId != null ? String(w.countryId) : "",
      stateId: w?.stateId != null ? String(w.stateId) : "",
      districtId: w?.districtId != null ? String(w.districtId) : "",
      cityId: w?.cityId != null ? String(w.cityId) : "",
      zipcode: toStr(w?.zipcode),
      contactPerson: toStr(w?.contactPerson),
      email: toStr(w?.email),
      mobile: toStr(w?.mobile),
      phone: toStr(w?.phone),
      remarks: toStr(w?.remarks),
      code: toStr(w?.code || codeFromInit),
      isClosed:
        w?.isClosed === true ||
        String(w?.isClosed) === "1" ||
        String(w?.isClosed).toLowerCase() === "true",
    });
  }, []);

  const loadInit = useCallback(async () => {
    try {
      if (!loginUserId) return toast.error("Login user not found");
      setPageLoading(true);
      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          loginUserId,
          branchId,
          workLocationId: isEdit ? workLocationId : undefined,
        },
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Failed to load initial data");
      setCountries(Array.isArray(data?.countries) ? data.countries : []);
      setStates(Array.isArray(data?.states) ? data.states : []);
      setDistricts(Array.isArray(data?.districts) ? data.districts : []);
      setCities(Array.isArray(data?.cities) ? data.cities : []);
      if (data?.workLocation) fillFormFromData(data.workLocation, data.code || "");
      else setFormData({ ...emptyForm, code: toStr(data?.code) });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load initial data");
    } finally {
      setPageLoading(false);
    }
  }, [loginUserId, branchId, isEdit, workLocationId, commonHeaders, fillFormFromData]);

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  const loadStates = useCallback(
    async (countryId) => {
      try {
        setLoadingDDL(true);
        const { data } = await axios.get(`${API_BASE}/states`, {
          params: { countryid: countryId },
          headers: commonHeaders,
        });
        if (!data?.success) throw new Error(data?.message || "Failed to load states");
        setStates(Array.isArray(data?.list) ? data.list : []);
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "Failed to load states");
        setStates([]);
      } finally {
        setLoadingDDL(false);
      }
    },
    [commonHeaders]
  );

  const loadDistricts = useCallback(
    async (stateId) => {
      try {
        setLoadingDDL(true);
        const { data } = await axios.get(`${API_BASE}/districts`, {
          params: { stateid: stateId },
          headers: commonHeaders,
        });
        if (!data?.success) throw new Error(data?.message || "Failed to load districts");
        setDistricts(Array.isArray(data?.list) ? data.list : []);
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "Failed to load districts");
        setDistricts([]);
      } finally {
        setLoadingDDL(false);
      }
    },
    [commonHeaders]
  );

  const loadCities = useCallback(
    async (districtId) => {
      try {
        setLoadingDDL(true);
        const { data } = await axios.get(`${API_BASE}/cities`, {
          params: { districtid: districtId },
          headers: commonHeaders,
        });
        if (!data?.success) throw new Error(data?.message || "Failed to load cities");
        setCities(Array.isArray(data?.list) ? data.list : []);
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "Failed to load cities");
        setCities([]);
      } finally {
        setLoadingDDL(false);
      }
    },
    [commonHeaders]
  );

  const onCountryChange = async (e) => {
    const countryId = e.target.value;
    setFormData((prev) => ({ ...prev, countryId, stateId: "", districtId: "", cityId: "" }));
    setStates([]);
    setDistricts([]);
    setCities([]);
    if (countryId) await loadStates(countryId);
  };

  const onStateChange = async (e) => {
    const stateId = e.target.value;
    setFormData((prev) => ({ ...prev, stateId, districtId: "", cityId: "" }));
    setDistricts([]);
    setCities([]);
    if (stateId) await loadDistricts(stateId);
  };

  const onDistrictChange = async (e) => {
    const districtId = e.target.value;
    setFormData((prev) => ({ ...prev, districtId, cityId: "" }));
    setCities([]);
    if (districtId) await loadCities(districtId);
  };

  const validate = () => {
    if (!toStr(formData.workLocName)) return toast.error("Please enter Work Location Name"), false;
    if (!toStr(formData.address1)) return toast.error("Please enter Address1"), false;
    if (!toStr(formData.countryId)) return toast.error("Please select Country"), false;
    if (!toStr(formData.stateId)) return toast.error("Please select State"), false;
    if (!toStr(formData.districtId)) return toast.error("Please select District"), false;
    if (!toStr(formData.cityId)) return toast.error("Please select City"), false;
    if (!toStr(formData.contactPerson)) return toast.error("Please enter Contact Person"), false;
    if (!toStr(formData.phone)) return toast.error("Please enter Phone"), false;
    if (!toStr(formData.code)) return toast.error("Code not generated"), false;
    if (!toInt(branchId)) return toast.error("branchId not found"), false;
    if (!toInt(loginUserId)) return toast.error("loginUserId not found"), false;
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      setLoading(true);
      const { data } = await axios.post(
        `${API_BASE}/save`,
        {
          workLocationId: isEdit ? workLocationId : 0,
          loginUserId: toInt(loginUserId, 0),
          branchId: toInt(branchId, 0),
          workLocName: toStr(formData.workLocName),
          code: toStr(formData.code),
          address1: toStr(formData.address1),
          address2: toStr(formData.address2),
          countryId: toInt(formData.countryId, 0),
          stateId: toInt(formData.stateId, 0),
          districtId: toInt(formData.districtId, 0),
          cityId: toInt(formData.cityId, 0),
          zipcode: toStr(formData.zipcode),
          contactPerson: toStr(formData.contactPerson),
          email: toStr(formData.email),
          mobile: toStr(formData.mobile),
          phone: toStr(formData.phone),
          remarks: toStr(formData.remarks),
          isClosed: !!formData.isClosed,
          branchType: branch,
        },
        { headers: commonHeaders }
      );
      if (!data?.success) throw new Error(data?.message || "Save failed");
      toast.success(data?.message || (isEdit ? "Updated Successfully" : "Inserted Successfully"));
      setTimeout(() => navigate("/vehicle/WorkLocationListing"), 700);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">WORK LOCATION MASTER</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              {isEdit ? "Edit Work Location" : "Add Work Location"}
            </h2>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate("/vehicle/WorkLocationListing")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                <FaArrowLeft /> Back
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={loading || pageLoading || loadingDDL}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg text-white ${
                  loading || pageLoading ? "opacity-60 cursor-not-allowed" : ""
                } ${buttonClass} ${hoverClass}`}
              >
                <FaSave />
                {loading ? "Saving..." : isEdit ? "Update" : "Save"}
              </button>
            </div>
          </div>

          {pageLoading ? (
            <div className={`p-16 text-center ${isDark ? "bg-gray-800/50" : "bg-blue-50"} rounded-lg`}>
              Loading...
            </div>
          ) : (
            <>
              <Section title="Location Details" isDark={isDark}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Field label="Work-Loc Name *" labelClass={labelClass}>
                    <input
                      value={formData.workLocName}
                      onChange={(e) => setField("workLocName", e.target.value)}
                      className={inputClass}
                      disabled={loading}
                    />
                  </Field>
                  <Field label="Code" labelClass={labelClass}>
                    <input value={formData.code} className={inputClass} readOnly />
                  </Field>
                  <Field label="Closed" labelClass={labelClass}>
                    <label className="flex items-center gap-2 h-10">
                      <input
                        type="checkbox"
                        checked={!!formData.isClosed}
                        onChange={(e) => setField("isClosed", e.target.checked)}
                        disabled={loading}
                      />
                      <span className="text-sm">Mark as closed</span>
                    </label>
                  </Field>
                  <Field label="Address1 *" labelClass={labelClass} span>
                    <textarea
                      value={formData.address1}
                      onChange={(e) => setField("address1", e.target.value)}
                      className={inputClass}
                      rows="2"
                      disabled={loading}
                    />
                  </Field>
                  <Field label="Address2" labelClass={labelClass} span>
                    <textarea
                      value={formData.address2}
                      onChange={(e) => setField("address2", e.target.value)}
                      className={inputClass}
                      rows="2"
                      disabled={loading}
                    />
                  </Field>
                </div>
              </Section>

              <Section title="Address / Region" isDark={isDark}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <Field label="Country *" labelClass={labelClass}>
                    <select
                      value={formData.countryId}
                      onChange={onCountryChange}
                      className={inputClass}
                      disabled={loading || loadingDDL}
                    >
                      <option value="">Select</option>
                      {countries.map((c) => (
                        <option key={c.id} value={String(c.id)}>{c.name}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="State *" labelClass={labelClass}>
                    <select
                      value={formData.stateId}
                      onChange={onStateChange}
                      className={inputClass}
                      disabled={loading || loadingDDL || !formData.countryId}
                    >
                      <option value="">Select</option>
                      {states.map((s) => (
                        <option key={s.id} value={String(s.id)}>{s.name}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="District *" labelClass={labelClass}>
                    <select
                      value={formData.districtId}
                      onChange={onDistrictChange}
                      className={inputClass}
                      disabled={loading || loadingDDL || !formData.stateId}
                    >
                      <option value="">Select</option>
                      {districts.map((d) => (
                        <option key={d.id} value={String(d.id)}>{d.name}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="City *" labelClass={labelClass}>
                    <select
                      value={formData.cityId}
                      onChange={(e) => setField("cityId", e.target.value)}
                      className={inputClass}
                      disabled={loading || loadingDDL || !formData.districtId}
                    >
                      <option value="">Select</option>
                      {cities.map((c) => (
                        <option key={c.id} value={String(c.id)}>{c.name}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Zipcode" labelClass={labelClass}>
                    <input
                      value={formData.zipcode}
                      onChange={(e) => setField("zipcode", e.target.value)}
                      className={inputClass}
                      disabled={loading}
                    />
                  </Field>
                </div>
              </Section>

              <Section title="Contact" isDark={isDark}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <Field label="Contact Person *" labelClass={labelClass}>
                    <input
                      value={formData.contactPerson}
                      onChange={(e) => setField("contactPerson", e.target.value)}
                      className={inputClass}
                      disabled={loading}
                    />
                  </Field>
                  <Field label="Phone *" labelClass={labelClass}>
                    <input
                      value={formData.phone}
                      onChange={(e) => setField("phone", e.target.value)}
                      className={inputClass}
                      disabled={loading}
                    />
                  </Field>
                  <Field label="Mobile" labelClass={labelClass}>
                    <input
                      value={formData.mobile}
                      onChange={(e) => setField("mobile", e.target.value)}
                      className={inputClass}
                      disabled={loading}
                    />
                  </Field>
                  <Field label="Email" labelClass={labelClass}>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setField("email", e.target.value)}
                      className={inputClass}
                      disabled={loading}
                    />
                  </Field>
                  <Field label="Remarks" labelClass={labelClass} span>
                    <textarea
                      value={formData.remarks}
                      onChange={(e) => setField("remarks", e.target.value)}
                      className={inputClass}
                      rows="2"
                      disabled={loading}
                    />
                  </Field>
                </div>
              </Section>
            </>
          )}

          <div className="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => (isEdit ? loadInit() : loadInit())}
              disabled={loading || pageLoading}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                isDark
                  ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                  : "border-gray-300 text-gray-700 hover:bg-gray-100"
              }`}
            >
              <FaUndo />
              {isEdit ? "Reload" : "Clear"}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={loading || pageLoading || loadingDDL}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${buttonClass} ${hoverClass}`}
            >
              <FaSave />
              {isEdit ? "Update" : "Save"}
            </button>
          </div>

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

function Section({ title, isDark, children }) {
  return (
    <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, labelClass, children, span }) {
  return (
    <div className={span ? "md:col-span-2" : ""}>
      <label className={labelClass}>{label}</label>
      {children}
    </div>
  );
}

export default WorkLocationMaster;